import dayjs from 'dayjs';
import { FormattedMessage, FormattedNumber } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// screen
import { DailyInvoiceModel } from 'modules/map/components/daily-consolidate-invoice/types';

interface Props {
  data: DailyInvoiceModel;
}
const MenuTable: React.FC<Props> = (props) => {
  const {
    data: {
      structuredData: { data, metadata },
    },
  } = props;
  const menuData = data.menu;
  const invoiceData = data.items || [];
  const showAfternoon = metadata?.hasAfternoon;

  const isInvoiceTypeOne = metadata?.settlementStatementType === 'offset';

  const invoiceTypeOne = () => {
    return (
      <>
        <TableRow>
          <TableCell>A</TableCell>
          <TableCell>Tổng chi trong ngày</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell>
            <FormattedNumber value={data.outcome.totalAmount} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>B</TableCell>
          <TableCell>
            Tổng thu trong ngày
            <br />
            (Đã trừ thuế GTGT 8%)
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.income?.quantity} />
          </TableCell>
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.income?.price} />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.income?.totalAmount} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>C</TableCell>
          <TableCell>Mang sang từ hôm trước</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.remainYesterday} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>D</TableCell>
          <TableCell>
            Chuyển sang ngày hôm sau
            <br />
            (D=B+C-A)
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.remainNextDay} />
          </TableCell>
        </TableRow>
      </>
    );
  };

  const invoiceTypeTwo = () => {
    return (
      <>
        <TableRow>
          <TableCell />
          <TableCell className="whitespace-pre-wrap">
            <FormattedMessage id="map.dailyOutcome" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.outcome?.quantity} />
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.outcome?.totalAmount} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell className="whitespace-pre-wrap">
            <FormattedMessage id="map.dailyIncome" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.income?.quantity} />
          </TableCell>
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.income?.price} />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.income?.totalAmount} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell className="whitespace-pre-wrap">
            <FormattedMessage id="map.tax" />
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell align="right">
            <FormattedNumber value={data.tax} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} />
          <TableCell className="whitespace-pre-wrap" colSpan={3}>
            <FormattedMessage id="map.priceAfterTax" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.incomeAfterTax} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} />
          <TableCell className="whitespace-pre-wrap" colSpan={3}>
            <FormattedMessage id="map.remainYesterday" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.remainYesterday} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} />
          <TableCell className="whitespace-pre-wrap" colSpan={3}>
            <FormattedMessage id="map.totalIncome" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.total} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} />
          <TableCell className="whitespace-pre-wrap" colSpan={3}>
            <FormattedMessage id="map.remain" />
          </TableCell>
          <TableCell align="right">
            <FormattedNumber value={data.remain} />
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <TableContainer
      className="px-2 tablet:px-4 space-y-4"
      sx={{
        mt: 2,
        '& td,th': {
          border: '0.5px solid',
          textAlign: 'center',
          padding: 1,
        },
        '@media print': {
          '& table': {
            borderCollapse: 'collapse',
          },
          '& td,th': {
            background: 'none',
            color: 'black',
          },
          '& thead': {
            background: 'none',
          },
        },
      }}
    >
      <Typography
        variant="h6"
        textAlign={'center'}
        sx={{
          display: 'none',
          '@media print': {
            display: 'block',
            color: 'black',
          },
        }}
      >
        <FormattedMessage id="map.dailyInvoice" />
        &nbsp;({dayjs().format('LL')})
      </Typography>
      <Table
        sx={(theme) => ({
          borderCollapse: 'separate',
          '& th, & td': { borderRight: `1px solid ${theme.palette.divider}` },
        })}
      >
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
          }}
        >
          <TableRow>
            {[
              {
                label: <FormattedMessage id="map.day" />,
              },
              {
                label: <FormattedMessage id="map.menu" />,
                width: '50%',
              },

              {
                label: <FormattedMessage id={showAfternoon ? 'map.afternoon' : 'map.note'} />,
                width: '30%',
              },
            ].map((item, index) => {
              return (
                <TableCell
                  key={index}
                  className="whitespace-pre-wrap"
                  style={{ width: item.width }}
                >
                  {item.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {menuData.dishes.map((dish, index) => {
            const isFistRow = index === 0;
            return (
              <TableRow key={index}>
                {isFistRow && (
                  <TableCell rowSpan={menuData.dishes.length}>
                    <span className="capitalize">{dayjs(menuData.datetime).format('dddd')}</span>
                    <br />
                    {dayjs(menuData.datetime).format('L')}
                    <br />
                    {menuData.studentCount && (
                      <p className="mt-4">
                        <FormattedNumber value={menuData.studentCount} />
                        &nbsp;
                        <FormattedMessage id="map.student" />
                      </p>
                    )}
                  </TableCell>
                )}
                <TableCell>{dish}</TableCell>
                {isFistRow && (
                  <TableCell rowSpan={menuData.dishes.length} style={{ textAlign: 'left' }}>
                    {showAfternoon ? (
                      menuData.afternoons?.map((val, index) => {
                        return <Box key={index}>{val}</Box>;
                      })
                    ) : (
                      <Box>{menuData.note}</Box>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Table
        sx={(theme) => ({
          borderCollapse: 'separate',
          '& th, & td': { borderRight: `1px solid ${theme.palette.divider}` },
        })}
      >
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
          }}
        >
          <TableRow>
            {[
              {
                label: <FormattedMessage id="stt" />,
                width: '5%',
              },
              {
                label: <FormattedMessage id="map.dishName" />,
                width: '30%',
              },
              {
                label: <FormattedMessage id="map.quantity" />,
                width: '15%',
              },
              {
                label: <FormattedMessage id="map.dvt" />,
                width: '10%',
              },
              {
                label: <FormattedMessage id="map.priceBill" />,
                width: '20%',
              },
              {
                label: <FormattedMessage id="map.finalPrice" />,
                width: '20%',
              },
            ].map((item, index) => {
              return (
                <TableCell
                  key={index}
                  className="whitespace-pre-wrap"
                  style={{ width: item.width }}
                >
                  {item.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ '& td': { fontWeight: 700 } }}>
          {invoiceData.map((value, index) => {
            const shouldDisplayRow = Boolean(value.name);

            return (
              <TableRow key={index} sx={{ '& td': { fontWeight: 400 } }}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{shouldDisplayRow ? value.name : ''}</TableCell>
                <TableCell align="right">
                  {shouldDisplayRow ? <FormattedNumber value={value.quantity} /> : ''}
                </TableCell>
                <TableCell align="center">{shouldDisplayRow ? value.unit : ''}</TableCell>
                <TableCell align="right">
                  {shouldDisplayRow ? <FormattedNumber value={value.price ?? 0} /> : ''}
                </TableCell>
                <TableCell align="right">
                  {shouldDisplayRow ? <FormattedNumber value={value.totalAmount} /> : ''}
                </TableCell>
              </TableRow>
            );
          })}
          {isInvoiceTypeOne ? invoiceTypeOne() : invoiceTypeTwo()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default MenuTable;
